import type {
  HREmployee,
  HRLeaveBalance,
  HRLeaveRequest,
  HROnboardingTask,
  HRPerformanceReview,
} from '@cloud/db'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, Check, Edit } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import type { LayoutManifest } from '@cloud/modules'
import { EmployeeAvatar } from '@/components/hr/employee-avatar'
import { EmployeeForm } from '@/components/hr/employee-form'
import { LinkedUserCard } from '@/components/hr/linked-user-card'
import { StatusBadge } from '@/components/hr/status-badge'
import { ModuleGuard } from '@/components/module-guard'
import { LayoutRenderer, type LayoutFieldMeta } from '@/components/custom/layout-renderer'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/hr/employees/$employeeId')({
  component: EmployeeDetailPage,
})

function EmployeeDetailPage() {
  return (
    <ModuleGuard moduleKey="hr">
      <EmployeeDetail />
    </ModuleGuard>
  )
}

function EmployeeDetail() {
  const params = Route.useParams()
  const employeeId = Number(params.employeeId)

  const [isFormOpen, setIsFormOpen] = useState(false)

  const {
    data: employee,
    isLoading,
    refetch,
  } = useQuery(orpc.hr.getEmployee.queryOptions({ input: { id: employeeId } }))
  const { data: onboardingData } = useQuery(
    orpc.hr.getOnboardingTasks.queryOptions({ input: { employeeId } }),
  )
  const { data: performanceData } = useQuery(
    orpc.hr.getEmployeePerformanceSummary.queryOptions({ input: { id: employeeId } }),
  )
  const { data: leaveBalance } = useQuery(
    orpc.hr.getLeaveBalance.queryOptions({ input: { employeeId } }),
  )
  const { data: leaveRequests } = useQuery(
    orpc.hr.getLeaveRequests.queryOptions({ input: { employeeId } }),
  )

  // P2.4: resolve the layout manifest for hr.employee. Returns the
  // tenant override if one exists, otherwise the module default
  // baked into the HR manifest, otherwise a synthesized fallback.
  const { data: layoutData } = useQuery(
    orpc.extensions.layouts.get.queryOptions({
      input: { moduleKey: 'hr', entityKey: 'employee', kind: 'detail' },
    }),
  )

  // P2.4: extension field definitions for hr.employee. Used by the
  // layout renderer to format extension field values (label, type,
  // select options). Safe to hit even if there are no fields — it
  // just returns an empty list.
  const { data: extFieldRows } = useQuery(
    orpc.extensions.entityFields.list.queryOptions({
      input: { moduleKey: 'hr', entityKey: 'employee' },
    }),
  )

  const emp = employee as
    | (HREmployee & {
        departmentInfo?: { id: number; name: string } | null
        managerInfo?: { id: number; firstName: string; lastName: string } | null
        leaveBalance?: HRLeaveBalance[]
        linkedUser?: { id: string; name: string; email: string } | null
        /** P2.3: custom field values joined in by the extension helper. */
        _ext?: Record<string, unknown>
        /**
         * P2.3: custom field definitions the tenant has added to
         * hr.employee. The detail page renders one read-only cell per
         * definition below.
         */
        _extFields?: Array<{
          fieldKey: string
          definition: Record<string, unknown>
        }>
      })
    | null

  const onboardingTasks = onboardingData as {
    pending: HROnboardingTask[]
    inProgress: HROnboardingTask[]
    completed: HROnboardingTask[]
    completionPercentage: number
  } | null

  const performanceReviews = (performanceData as { reviews: HRPerformanceReview[] })?.reviews ?? []
  const leaveBalances = (leaveBalance as HRLeaveBalance[]) ?? []
  const leaveReqs = (leaveRequests as HRLeaveRequest[]) ?? []

  const updateTaskMutation = useMutation(
    orpc.hr.updateOnboardingTask.mutationOptions({
      onSuccess: () => {
        toast.success('Task updated')
        refetch()
      },
      onError: (e) => toast.error(e.message),
    }),
  )

  const handleTaskStatusChange = async (
    taskId: number,
    status: 'pending' | 'in-progress' | 'completed',
  ) => {
    await updateTaskMutation.mutateAsync({ taskId, status })
  }

  if (isLoading) {
    return (
      <div className="container mx-auto py-6 px-4">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!emp) {
    return (
      <div className="container mx-auto py-6 px-4">
        <p>Employee not found</p>
      </div>
    )
  }

  const fullName = `${emp.firstName ?? ``} ${emp.lastName ?? ``}`.trim()

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6 flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => window.history.back()}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>

      <div className="mb-6 flex items-start justify-between">
        <div className="flex items-center gap-4">
          <EmployeeAvatar employee={emp} showName showTitle />
          <div>
            <h1 className="text-2xl font-bold">{fullName}</h1>
            <p className="text-muted-foreground">{emp.jobTitle ?? 'No job title'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={emp.status ?? 'active'} />
          <Button variant="outline" onClick={() => setIsFormOpen(true)}>
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
        </div>
      </div>

      <Tabs defaultValue="profile" className="w-full">
        <TabsList>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="onboarding">Onboarding</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="leave">Leave</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="space-y-4">
          <LinkedUserCard
            employeeId={employeeId}
            linkedUser={emp.linkedUser ?? null}
            onChange={() => refetch()}
          />
          {/*
            P2.4: Profile tab content is rendered from the layout
            manifest (either tenant override or HR module default).
            The hardcoded Personal/Employment/Additional Info cards
            that used to live here are gone — the default layout in
            hr.manifest.ts produces the same cards, and tenants can
            override via /settings/extensions/hr/employee/layout.
            Only kicks in once both the layout and the field meta
            have loaded; otherwise a skeleton stands in.
          */}
          {layoutData && extFieldRows ? (
            <LayoutRenderer
              // The backend's layout payload is loosely typed at the
              // zod boundary (blocks are z.unknown) — cast it to the
              // strict LayoutManifest here. The backend validator
              // guarantees the structure is correct.
              layout={layoutData.layout as unknown as LayoutManifest}
              record={buildLayoutRecord(emp)}
              coreFields={new Map<string, LayoutFieldMeta>(
                layoutData.coreFields.map((f) => [
                  f.key,
                  {
                    label: f.label,
                    type: f.type,
                    options: f.options,
                  },
                ]),
              )}
              extensionFields={new Map<string, LayoutFieldMeta>(
                extFieldRows.map((f) => {
                  const def = f.definition as {
                    label?: string
                    type?: string
                    options?: Array<{ value: string; label: string }>
                  }
                  return [
                    f.fieldKey,
                    {
                      label: def.label ?? f.fieldKey,
                      type: def.type ?? 'text',
                      options: def.options,
                    },
                  ]
                }),
              )}
            />
          ) : (
            <Skeleton className="h-64 w-full" />
          )}
        </TabsContent>

        <TabsContent value="onboarding">
          <Card>
            <CardHeader>
              <CardTitle>Onboarding Progress</CardTitle>
              <div className="flex items-center gap-4 mt-2">
                <Progress value={onboardingTasks?.completionPercentage ?? 0} className="flex-1" />
                <span className="text-sm font-medium">
                  {onboardingTasks?.completionPercentage ?? 0}%
                </span>
              </div>
            </CardHeader>
            <CardContent>
              {onboardingTasks &&
              (onboardingTasks.pending.length > 0 ||
                onboardingTasks.inProgress.length > 0 ||
                onboardingTasks.completed.length > 0) ? (
                <div className="space-y-6">
                  {onboardingTasks.pending.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2">Pending</h4>
                      <Table>
                        <TableBody>
                          {onboardingTasks.pending.map((task) => (
                            <TableRow key={task.id}>
                              <TableCell>{task.taskName}</TableCell>
                              <TableCell>
                                <Badge variant="outline">{task.priority}</Badge>
                              </TableCell>
                              <TableCell>
                                {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : '-'}
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleTaskStatusChange(task.id, 'in-progress')}
                                >
                                  Start
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                  {onboardingTasks.inProgress.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2">In Progress</h4>
                      <Table>
                        <TableBody>
                          {onboardingTasks.inProgress.map((task) => (
                            <TableRow key={task.id}>
                              <TableCell>{task.taskName}</TableCell>
                              <TableCell>
                                <Badge variant="outline">{task.priority}</Badge>
                              </TableCell>
                              <TableCell>
                                {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : '-'}
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  size="sm"
                                  onClick={() => handleTaskStatusChange(task.id, 'completed')}
                                >
                                  <Check className="h-4 w-4 mr-1" />
                                  Complete
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                  {onboardingTasks.completed.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2">Completed</h4>
                      <Table>
                        <TableBody>
                          {onboardingTasks.completed.map((task) => (
                            <TableRow key={task.id}>
                              <TableCell className="text-muted-foreground">
                                <Check className="h-4 w-4 inline mr-2 text-green-500" />
                                {task.taskName}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline">{task.priority}</Badge>
                              </TableCell>
                              <TableCell>
                                {task.completedAt
                                  ? new Date(task.completedAt).toLocaleDateString()
                                  : '-'}
                              </TableCell>
                              <TableCell className="text-right">
                                <Badge variant="default" className="bg-green-500">
                                  Done
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">No onboarding tasks</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance">
          <Card>
            <CardHeader>
              <CardTitle>Performance Reviews</CardTitle>
            </CardHeader>
            <CardContent>
              {performanceReviews.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Period</TableHead>
                      <TableHead>Cycle</TableHead>
                      <TableHead>Rating</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {performanceReviews.map((review) => (
                      <TableRow key={review.id}>
                        <TableCell>
                          {review.reviewPeriodStart && review.reviewPeriodEnd
                            ? `${new Date(review.reviewPeriodStart).toLocaleDateString()} - ${new Date(review.reviewPeriodEnd).toLocaleDateString()}`
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{review.reviewCycle}</Badge>
                        </TableCell>
                        <TableCell>
                          {review.overallRating ? (
                            <div className="flex items-center gap-1">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <span
                                  key={star}
                                  className={
                                    star <= review.overallRating!
                                      ? 'text-yellow-500'
                                      : 'text-gray-300'
                                  }
                                >
                                  ★
                                </span>
                              ))}
                            </div>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={review.status} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No performance reviews yet
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="leave">
          <Card>
            <CardHeader>
              <CardTitle>Leave Balances</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {leaveBalances.map((balance) => (
                  <Card key={balance.id}>
                    <CardContent className="pt-4">
                      <div className="flex justify-between mb-2">
                        <span className="font-medium capitalize">{balance.leaveType}</span>
                        <span className="text-sm text-muted-foreground">
                          {balance.remaining}/{balance.totalAllowed} days
                        </span>
                      </div>
                      <Progress
                        value={(balance.used / balance.totalAllowed) * 100}
                        className="h-2"
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        Used: {balance.used} days
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <h4 className="font-medium mb-4">Leave Requests</h4>
              {leaveReqs.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Start Date</TableHead>
                      <TableHead>End Date</TableHead>
                      <TableHead>Days</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leaveReqs.map((request) => (
                      <TableRow key={request.id}>
                        <TableCell className="capitalize">{request.leaveType}</TableCell>
                        <TableCell>{new Date(request.startDate).toLocaleDateString()}</TableCell>
                        <TableCell>{new Date(request.endDate).toLocaleDateString()}</TableCell>
                        <TableCell>{request.totalDays}</TableCell>
                        <TableCell>
                          <StatusBadge status={request.status} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">No leave requests</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <EmployeeForm
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        employee={emp}
        onSuccess={() => refetch()}
      />
    </div>
  )
}

/**
 * P2.4: shape the employee row for the LayoutRenderer. We pass the
 * row through mostly unchanged — the renderer reads core fields as
 * top-level keys and extension values under `_ext` — but coerce a
 * few derived fields (manager name, department name) so they render
 * nicely when referenced by `kind: 'field'` blocks. This is the
 * escape hatch for fields whose core value is an ID but the user
 * wants to see a friendly name.
 */
function buildLayoutRecord(
  emp: Record<string, unknown> & {
    departmentInfo?: { name: string } | null
    managerInfo?: { firstName: string; lastName: string } | null
    _ext?: Record<string, unknown>
  },
): Record<string, unknown> & { _ext?: Record<string, unknown> } {
  return {
    ...emp,
    // Override departmentId rendering with the resolved name when
    // the layout references `departmentId` as a `field` block.
    departmentId: emp.departmentInfo?.name ?? (emp as { department?: string }).department ?? null,
    managerId: emp.managerInfo
      ? `${emp.managerInfo.firstName} ${emp.managerInfo.lastName}`
      : null,
    _ext: emp._ext ?? {},
  }
}
